import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Select, Input, InputNumber, Button, Tag, Alert, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { fetchSchoolsMeta, fetchSchoolsRows, createSchool, ApiError } from './schoolsApi';
import BasicLayout from '../../components/layout/BasicLayout';
import './SchoolsPage.css';

const { Option } = Select;
const { TextArea } = Input;

const USER_ACTIONS_PATH = '/admin/user-actions';

const EMPTY_FORM = {
  name: '',
  address: '',
  shift: 2,
  lang_kz: false,
  lang_ru: false,
  capacity: '',
  students: '',
  teachers: '',
};

const langCode = (kz, ru) => (kz && ru ? 'kz_ru' : kz ? 'kz' : ru ? 'ru' : '');

export default function SchoolsPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = (i18n.language || '').toLowerCase().startsWith('ru') ? 'ru' : 'kk';

  const [meta, setMeta] = useState({ shifts: [], languages: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [fltShift, setFltShift] = useState('');
  const [fltLang, setFltLang] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([fetchSchoolsMeta(), fetchSchoolsRows()])
      .then(([m, list]) => {
        setMeta({
          shifts: m && m.shifts ? m.shifts : [],
          languages: m && m.languages ? m.languages : [],
        });
        setRows(list && Array.isArray(list.rows) ? list.rows : []);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shiftLabel = useCallback(
    (value) => {
      const s = meta.shifts.find((x) => x.value === value);
      if (s) return lang === 'kk' ? s.name_kk : s.name_ru;
      return value === 1 ? t('schools.shift1') : t('schools.shift2');
    },
    [meta.shifts, lang, t]
  );

  const langLabel = useCallback(
    (kz, ru) => {
      const code = langCode(kz, ru);
      const l = meta.languages.find((x) => x.code === code);
      if (l) return lang === 'kk' ? l.name_kk : l.name_ru;
      if (code === 'kz_ru') return t('schools.langBoth');
      if (code === 'kz') return t('schools.langKz');
      if (code === 'ru') return t('schools.langRu');
      return '—';
    },
    [meta.languages, lang, t]
  );

  const fmtInt = useCallback(
    (n) =>
      n === null || n === undefined
        ? null
        : Number(n).toLocaleString(lang === 'kk' ? 'kk-KZ' : 'ru-RU', {
            maximumFractionDigits: 0,
          }),
    [lang]
  );

  const shiftFilterOptions = useMemo(
    () =>
      meta.shifts.length
        ? meta.shifts.map((s) => ({ value: s.value, label: shiftLabel(s.value) }))
        : [
            { value: 1, label: t('schools.shift1') },
            { value: 2, label: t('schools.shift2') },
          ],
    [meta.shifts, shiftLabel, t]
  );

  const view = useMemo(
    () =>
      rows.filter((r) => {
        if (fltShift !== '' && r.shift !== fltShift) return false;
        if (fltLang !== '' && langCode(r.lang_kz, r.lang_ru) !== fltLang) return false;
        return true;
      }),
    [rows, fltShift, fltLang]
  );

  const columns = [
    {
      title: t('schools.colSchool'),
      key: 'school',
      width: '38%',
      render: (_, r) => (
        <div>
          <div className="sch-cell-name">{r.name}</div>
          <div className="sch-cell-sub">{r.address}</div>
        </div>
      ),
    },
    {
      title: t('schools.colCap'),
      key: 'capacity',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="sch-num">{fmtInt(r.capacity)}</span>,
    },
    {
      title: t('schools.colStudents'),
      key: 'students',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="sch-num">{fmtInt(r.students)}</span>,
    },
    {
      title: t('schools.colTeachers'),
      key: 'teachers',
      align: 'right',
      width: 120,
      render: (_, r) => <span className="sch-num">{fmtInt(r.teachers)}</span>,
    },
    {
      title: t('schools.colShift'),
      key: 'shift',
      width: 140,
      render: (_, r) => <Tag className="sch-pill">{shiftLabel(r.shift)}</Tag>,
    },
    {
      title: t('schools.colLang'),
      key: 'lang',
      width: 190,
      render: (_, r) => (
        <Tag className="sch-pill sch-pill--q">{langLabel(r.lang_kz, r.lang_ru)}</Tag>
      ),
    },
  ];

  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const cp = { ...prev };
      delete cp[field];
      return cp;
    });

  const openModal = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  };
  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const computed = useMemo(() => {
    const cap = form.capacity === '' ? null : Number(form.capacity);
    const st = form.students === '' ? null : Number(form.students);
    if (cap === null || st === null || Number.isNaN(cap) || Number.isNaN(st)) {
      return { deficit: null, surplus: null };
    }
    return { deficit: Math.max(st - cap, 0), surplus: Math.max(cap - st, 0) };
  }, [form.capacity, form.students]);

  const isInt = (v) => /^\d+$/.test(String(v).trim());

  // Клиентская валидация (АС2)
  const validate = () => {
    const e = {};
    if (!form.name || !form.name.trim()) e.name = 'reqMsg';
    if (!form.address || !form.address.trim()) e.address = 'reqMsg';
    if (!form.lang_kz && !form.lang_ru) e.lang = 'langMsg';

    if (String(form.capacity).trim() === '') e.capacity = 'reqMsg';
    else if (!isInt(form.capacity) || Number(form.capacity) < 1) e.capacity = 'capMsg';

    if (String(form.students).trim() === '') e.students = 'reqMsg';
    else if (!isInt(form.students) || Number(form.students) < 0) e.students = 'numMsg';

    if (String(form.teachers).trim() === '') e.teachers = 'reqMsg';
    else if (!isInt(form.teachers) || Number(form.teachers) < 0) e.teachers = 'numMsg';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.sch-field--err');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    address: form.address.trim(),
    shift: Number(form.shift),
    lang_kz: !!form.lang_kz,
    lang_ru: !!form.lang_ru,
    capacity: Number(form.capacity),
    students: Number(form.students),
    teachers: Number(form.teachers),
  });

  const save = async () => {
    if (submitting) return;
    if (!validate()) {
      scrollToFirstError();
      return;
    }
    setSubmitting(true);
    try {
      const created = await createSchool(buildPayload());
      setRows((prev) => [created, ...prev]);
      setFltShift('');
      setFltLang('');
      setModalOpen(false);
      notification.success({
        message: t('schools.toastOk'),
        description: (
          <span>
            {t('schools.toastSub')}{' '}
            <a onClick={() => history.push(USER_ACTIONS_PATH)}>{t('schools.toastLog')}</a>
          </span>
        ),
        placement: 'bottomRight',
        duration: 4,
      });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const parseServerError = (body) => {
    const raw = body && typeof body.error === 'string' ? body.error : '';
    const code = raw.includes(':') ? raw.slice(0, raw.indexOf(':')).trim() : raw.trim();
    const msg = raw.includes(':') ? raw.slice(raw.indexOf(':') + 1).trim() : raw.trim();
    const parts = msg.split('/').map((s) => s.trim());
    const text = (lang === 'kk' && parts[1] ? parts[1] : parts[0]) || '';
    return { code, text };
  };

  const handleSaveError = (err) => {
    const status = err instanceof ApiError ? err.status : 0;
    const body = (err && err.body) || {};
    const { code, text } = parseServerError(body);

    if (code === 'duplicate_name' || status === 409) {
      setErrors((prev) => ({
        ...prev,
        name: text || t('schools.dupMsg'),
        dup: text || t('schools.dupMsg'),
      }));
      scrollToFirstError();
      return;
    }

    if (status === 422 && body.fields) {
      const fields = body.fields;
      const names = Array.isArray(fields) ? fields : Object.keys(fields);
      const e = {};
      names.forEach((f) => {
        const key = f && f.field ? f.field : f;
        e[key] = 'reqMsg';
      });
      setErrors((prev) => ({ ...prev, ...e }));
      scrollToFirstError();
      return;
    }

    if (text) {
      setErrors((prev) => ({ ...prev, dup: text }));
      scrollToFirstError();
      return;
    }
    notification.error({ message: t('schools.saveError'), placement: 'bottomRight' });
  };

  const errText = (field) => {
    const v = errors[field];
    if (!v) return '';
    return ['reqMsg', 'numMsg', 'capMsg', 'langMsg', 'dupMsg'].includes(v) ? t(`schools.${v}`) : v;
  };
  const fieldCls = (field) => `sch-field${errors[field] ? ' sch-field--err' : ''}`;

  return (
    <BasicLayout>
      <div className="sch-page">
        <div className="sch-head">
          <h1 className="sch-title">{t('schools.title')}</h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
            {t('schools.create')}
          </Button>
        </div>

        <div className="sch-toolbar">
          <span className="sch-flt-lbl">{t('schools.fltShift')}</span>
          <Select
            allowClear
            value={fltShift === '' ? undefined : fltShift}
            onChange={(v) => setFltShift(v === undefined ? '' : v)}
            placeholder={t('schools.allShifts')}
            style={{ width: 180 }}
          >
            {shiftFilterOptions.map((s) => (
              <Option key={s.value} value={s.value}>
                {s.label}
              </Option>
            ))}
          </Select>

          <span className="sch-flt-lbl">{t('schools.fltLang')}</span>
          <Select
            allowClear
            value={fltLang === '' ? undefined : fltLang}
            onChange={(v) => setFltLang(v === undefined ? '' : v)}
            placeholder={t('schools.allLangs')}
            style={{ width: 220 }}
          >
            <Option value="kz">{t('schools.langKz')}</Option>
            <Option value="ru">{t('schools.langRu')}</Option>
            <Option value="kz_ru">{t('schools.langBoth')}</Option>
          </Select>
        </div>

        {loadError ? (
          <Alert
            type="warning"
            showIcon
            className="sch-alert"
            message={t('schools.loadError')}
            action={
              <Button size="small" onClick={load}>
                {t('schools.retry')}
              </Button>
            }
          />
        ) : (
          <div className="sch-card">
            <Table
              className="sch-table"
              rowKey="id"
              columns={columns}
              dataSource={view}
              loading={loading}
              locale={{ emptyText: <div className="sch-empty">{t('schools.noRows')}</div> }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) =>
                  `${t('schools.shown')} ${total} ${t('schools.of')} ${rows.length}`,
              }}
            />
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="sch-overlay">
          <div className="sch-modal" role="dialog" aria-modal="true">
            <div className="sch-modal-head">
              <h3>{t('schools.mTitle')}</h3>
              <button className="sch-modal-x" onClick={closeModal} aria-label="close">
                ✕
              </button>
            </div>

            <div className="sch-modal-body">
              <div className={fieldCls('name')}>
                <label>
                  {t('schools.fName')}
                  <span className="sch-req">*</span>
                </label>
                <Input
                  value={form.name}
                  maxLength={250}
                  showCount
                  placeholder={t('schools.phName')}
                  onChange={(e) => {
                    patch({ name: e.target.value });
                    clearError('name');
                    clearError('dup');
                  }}
                />
                {errors.name && <div className="sch-msg">{errText('name')}</div>}
              </div>

              <div className={fieldCls('address')}>
                <label>
                  {t('schools.fAddr')}
                  <span className="sch-req">*</span>
                </label>
                <TextArea
                  value={form.address}
                  maxLength={500}
                  showCount
                  rows={2}
                  placeholder={t('schools.phAddr')}
                  onChange={(e) => {
                    patch({ address: e.target.value });
                    clearError('address');
                  }}
                />
                {errors.address && <div className="sch-msg">{errText('address')}</div>}
              </div>

              <div className="sch-field">
                <label>
                  {t('schools.fShift')}
                  <span className="sch-req">*</span>
                </label>
                <div className="sch-seg">
                  {shiftFilterOptions.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className={form.shift === s.value ? 'on' : ''}
                      onClick={() => patch({ shift: s.value })}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={fieldCls('lang')}>
                <label>
                  {t('schools.fLang')}
                  <span className="sch-req">*</span>
                </label>
                <div className="sch-checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.lang_kz}
                      onChange={(e) => {
                        patch({ lang_kz: e.target.checked });
                        clearError('lang');
                      }}
                    />
                    {t('schools.langKz')}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.lang_ru}
                      onChange={(e) => {
                        patch({ lang_ru: e.target.checked });
                        clearError('lang');
                      }}
                    />
                    {t('schools.langRu')}
                  </label>
                </div>
                {errors.lang && <div className="sch-msg">{errText('lang')}</div>}
              </div>

              <div className="sch-grp-cap">{t('schools.grpCap')}</div>
              <div className="sch-row2">
                <div className={fieldCls('capacity')}>
                  <label>
                    {t('schools.fCap')}
                    <span className="sch-req">*</span>
                  </label>
                  <InputNumber
                    value={form.capacity === '' ? undefined : form.capacity}
                    min={1}
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                      patch({ capacity: v === null || v === undefined ? '' : v });
                      clearError('capacity');
                    }}
                  />
                  <div className="sch-hint">{t('schools.hintCap')}</div>
                  {errors.capacity && <div className="sch-msg">{errText('capacity')}</div>}
                </div>

                <div className={fieldCls('students')}>
                  <label>
                    {t('schools.fStudents')}
                    <span className="sch-req">*</span>
                  </label>
                  <InputNumber
                    value={form.students === '' ? undefined : form.students}
                    min={0}
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                      patch({ students: v === null || v === undefined ? '' : v });
                      clearError('students');
                    }}
                  />
                  <div className="sch-hint">{t('schools.hintStudents')}</div>
                  {errors.students && <div className="sch-msg">{errText('students')}</div>}
                </div>
              </div>

              <div className="sch-row2">
                <div className={fieldCls('teachers')}>
                  <label>
                    {t('schools.fTeachers')}
                    <span className="sch-req">*</span>
                  </label>
                  <InputNumber
                    value={form.teachers === '' ? undefined : form.teachers}
                    min={0}
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                      patch({ teachers: v === null || v === undefined ? '' : v });
                      clearError('teachers');
                    }}
                  />
                  <div className="sch-hint">{t('schools.hintTeachers')}</div>
                  {errors.teachers && <div className="sch-msg">{errText('teachers')}</div>}
                </div>
                <div />
              </div>

              <div className="sch-grp-cap">{t('schools.grpAuto')}</div>
              <div className="sch-row2">
                <div className="sch-field">
                  <label>{t('schools.fDeficit')}</label>
                  <Input
                    className="sch-ro"
                    readOnly
                    tabIndex={-1}
                    value={computed.deficit === null ? '' : fmtInt(computed.deficit)}
                    placeholder={t('schools.autoFill')}
                  />
                  <div className="sch-hint">{t('schools.hintDeficit')}</div>
                </div>
                <div className="sch-field">
                  <label>{t('schools.fSurplus')}</label>
                  <Input
                    className="sch-ro"
                    readOnly
                    tabIndex={-1}
                    value={computed.surplus === null ? '' : fmtInt(computed.surplus)}
                    placeholder={t('schools.autoFill')}
                  />
                  <div className="sch-hint">{t('schools.hintSurplus')}</div>
                </div>
              </div>

              {errors.dup && !errors.name && <div className="sch-dup-msg">{errText('dup')}</div>}
            </div>

            <div className="sch-modal-foot">
              <Button onClick={closeModal} disabled={submitting}>
                {t('schools.cancel')}
              </Button>
              <Button type="primary" onClick={save} loading={submitting}>
                {t('schools.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </BasicLayout>
  );
}
